import { useState, useEffect, useRef, useMemo } from "react";
import { useApp } from "../context/AppContext";
import { imagingAPI } from "../utils/api";

const MODALITIES = ["X-Ray", "CT", "MRI", "Ultrasound", "Mammography", "ECG", "Other"];
const DATE_OPTIONS = ["All", "Today", "This Week", "This Month", "This Year"];
const BLOOD_GROUPS = ["A+","A-","B+","B-","AB+","AB-","O+","O-","Unknown"];

const MODALITY_COLOR = {
  "X-Ray":       { color: "#4F46E5", bg: "#EEF2FF" },
  "CT":          { color: "#0D9488", bg: "#CCFBF1" },
  "MRI":         { color: "#7C3AED", bg: "#F5F3FF" },
  "Ultrasound":  { color: "#D97706", bg: "#FEF3C7" },
  "Mammography": { color: "#DB2777", bg: "#FDF2F8" },
  "ECG":         { color: "#059669", bg: "#DCFCE7" },
  "Other":       { color: "#64748B", bg: "#F1F5F9" },
};

function getDateRange(filter) {
  const now = new Date();
  const today = now.toISOString().split("T")[0];
  if (filter === "Today") return { from: today, to: today };
  if (filter === "This Week")  { const d = new Date(now); d.setDate(d.getDate()-7); return { from: d.toISOString().split("T")[0], to: today }; }
  if (filter === "This Month") { const d = new Date(now.getFullYear(), now.getMonth(), 1); return { from: d.toISOString().split("T")[0], to: today }; }
  if (filter === "This Year")  { const d = new Date(now.getFullYear(), 0, 1); return { from: d.toISOString().split("T")[0], to: today }; }
  return null;
}

function validatePhoneDigits(digits) {
  const d = digits.trim().replace(/[\s\-()]/g, "");
  if (!d) return { valid: false, message: "Phone is required." };
  if (!/^\d{10}$/.test(d)) return { valid: false, message: "Enter 10 digits after +880." };
  if (!/^1[3-9]\d{8}$/.test(d)) return { valid: false, message: "Must start with 13–19." };
  return { valid: true, normalised: "+880" + d };
}

export default function Imaging() {
  const { patients, user, addPatient, addImaging, deleteImaging, updateImaging, getImagingFileUrl } = useApp();
  const [allImaging, setAllImaging]     = useState([]);
  const [loading, setLoading]           = useState(false);
  const [search, setSearch]             = useState("");
  const [modalityFilter, setModality]   = useState("All");
  const [dateFilter, setDateFilter]     = useState("All");
  const [showUpload, setShowUpload]     = useState(false);
  const [viewImg, setViewImg]           = useState(null);
  const [editFindings, setEditFindings] = useState(null);
  const [findingsText, setFindingsText] = useState("");
  const [zoom, setZoom]                 = useState(1);
  const [confirmDel, setConfirmDel]     = useState(null);
  const [view, setView]                 = useState("register"); // "register" | "grid"
  const imgRef = useRef(null);

  // Persist viewImg across reload
  const handleSetViewImg = (img) => {
    if (img) {
      sessionStorage.setItem("emr_imaging_view", img.id);
    } else {
      sessionStorage.removeItem("emr_imaging_view");
    }
    setViewImg(img);
    setZoom(1);
  };

  useEffect(() => {
    setLoading(true);
    Promise.all(
      patients.map(p => imagingAPI.getForPatient(p.id).catch(() => []))
    ).then(results => {
      const all = results.flatMap((imgs, i) =>
        imgs.map(img => ({ ...img, patientName: patients[i]?.name, patientId: patients[i]?.id }))
      );
      all.sort((a, b) => new Date(b.created_at || b.date) - new Date(a.created_at || a.date));
      setAllImaging(all);
      setLoading(false);

      // Restore viewImg from sessionStorage after data loads
      const savedId = sessionStorage.getItem("emr_imaging_view");
      if (savedId) {
        const found = all.find(img => img.id === savedId);
        if (found) setViewImg(found);
      }
    });
  }, [patients]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const dateRange = getDateRange(dateFilter);
    return allImaging.filter(img => {
      if (q && !(
        img.patientName?.toLowerCase().includes(q) ||
        img.modality?.toLowerCase().includes(q) ||
        img.description?.toLowerCase().includes(q) ||
        img.radiologist?.toLowerCase().includes(q) ||
        img.patientId?.toLowerCase().includes(q)
      )) return false;
      if (modalityFilter !== "All" && img.modality !== modalityFilter) return false;
      if (dateRange && (img.date < dateRange.from || img.date > dateRange.to)) return false;
      return true;
    });
  }, [allImaging, search, modalityFilter, dateFilter]);

  const stats = useMemo(() => ({
    total: filtered.length,
    today: filtered.filter(i => i.date === new Date().toISOString().split("T")[0]).length,
    modalities: [...new Set(filtered.map(i => i.modality))].length,
    patients: [...new Set(filtered.map(i => i.patientId))].length,
  }), [filtered]);

  const canEdit   = user?.role === "doctor" || user?.role === "admin";
  const canUpload = ["technician","doctor","admin"].includes(user?.role);

  const handleSaveFindings = async (img) => {
    await updateImaging(img.id, { findings: findingsText });
    if (viewImg?.id === img.id) setViewImg(v => ({ ...v, findings: findingsText }));
    setAllImaging(prev => prev.map(i => i.id === img.id ? { ...i, findings: findingsText } : i));
    setEditFindings(null);
  };

  const handleDelete = async (id) => {
    await deleteImaging(id);
    setAllImaging(prev => prev.filter(i => i.id !== id));
    if (viewImg?.id === id) handleSetViewImg(null);
    setConfirmDel(null);
  };

  const isImageFile = (path) => /\.(jpg|jpeg|png|webp|bmp|gif|tiff)$/i.test(path || "");

  // ── Image viewer ──────────────────────────────────────────────────────────
  if (viewImg) {
    const mc = MODALITY_COLOR[viewImg.modality] || MODALITY_COLOR.Other;
    const fileUrl = getImagingFileUrl(viewImg.id);
    const isImg = isImageFile(viewImg.file_name);
    return (
      <div>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:20 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => handleSetViewImg(null)}>← Back</button>
          <div style={{ flex:1 }}>
            <h2 style={{ fontSize:17, fontWeight:700, marginBottom:2 }}>{viewImg.modality} — {viewImg.patientName}</h2>
            <div style={{ fontSize:12, color:"#64748B" }}>{viewImg.date}{viewImg.time ? ` · ${viewImg.time}` : ""}</div>
          </div>
          {canEdit && <button className="btn btn-danger btn-sm" onClick={() => setConfirmDel(viewImg.id)}>Delete</button>}
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 340px", gap:18 }}>
          <div className="card" style={{ overflow:"hidden" }}>
            <div className="card-header">
              <div style={{ fontWeight:700, fontSize:14 }}>
                <span style={{ background:mc.bg, color:mc.color, padding:"2px 10px", borderRadius:6, fontSize:12, fontWeight:700, marginRight:8 }}>{viewImg.modality}</span>
                {viewImg.file_name}
              </div>
              <div style={{ display:"flex", gap:6 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => setZoom(z => Math.max(0.5,z-0.25))}>−</button>
                <span style={{ fontSize:12, padding:"4px 8px", color:"#475569" }}>{Math.round(zoom*100)}%</span>
                <button className="btn btn-ghost btn-sm" onClick={() => setZoom(z => Math.min(4,z+0.25))}>+</button>
                <button className="btn btn-ghost btn-sm" onClick={() => setZoom(1)}>Reset</button>
                <a href={fileUrl} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm">Open</a>
              </div>
            </div>
            <div style={{ background:"#0F172A", minHeight:480, overflow:"auto", padding:24, display:"flex", alignItems:"flex-start", justifyContent:"flex-start" }}>
              {isImg ? (
                <img ref={imgRef} src={fileUrl} alt={viewImg.description||viewImg.modality}
                  style={{ transform:`scale(${zoom})`, transformOrigin:"top left", maxWidth:"none", transition:"transform 0.15s", imageRendering:zoom>2?"pixelated":"auto" }}
                  onError={e => { e.target.style.display="none"; }} />
              ) : (
                <div style={{ textAlign:"center", color:"#94A3B8", width:"100%" }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="64" height="64"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  <div style={{ fontSize:14, marginBottom:8 }}>{viewImg.file_name}</div>
                  <a href={fileUrl} target="_blank" rel="noreferrer" className="btn btn-primary btn-sm">Download / Open</a>
                </div>
              )}
            </div>
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
            <div className="card">
              <div className="card-body">
                <div style={{ fontSize:11, fontWeight:700, color:"#94A3B8", textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:10 }}>Study Info</div>
                {[["Patient",viewImg.patientName],["EMR ID",viewImg.patientId],["Modality",viewImg.modality],["Date",viewImg.date],["Time",viewImg.time||"—"],["Description",viewImg.description||"—"],["Radiologist",viewImg.radiologist||"—"]].map(([k,v]) => (
                  <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"5px 0", borderBottom:"1px solid #F1F5F9", fontSize:13 }}>
                    <span style={{ color:"#64748B" }}>{k}</span>
                    <span style={{ fontWeight:600, textAlign:"right", maxWidth:180 }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="card" style={{ flex:1 }}>
              <div className="card-header">
                <div style={{ fontWeight:700, fontSize:14 }}>Radiologist Notes / Findings</div>
                {canEdit && editFindings !== viewImg.id && (
                  <button className="btn btn-secondary btn-sm" onClick={() => { setEditFindings(viewImg.id); setFindingsText(viewImg.findings||""); }}>Edit</button>
                )}
              </div>
              <div className="card-body">
                {editFindings === viewImg.id ? (
                  <div>
                    <textarea className="input" value={findingsText} onChange={e => setFindingsText(e.target.value)} rows={8} style={{ resize:"vertical", fontSize:13 }} placeholder="Enter radiologist findings..." autoFocus />
                    <div style={{ display:"flex", gap:8, marginTop:10 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => setEditFindings(null)}>Cancel</button>
                      <button className="btn btn-primary btn-sm" onClick={() => handleSaveFindings(viewImg)}>Save Findings</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ whiteSpace:"pre-wrap", fontSize:13, color:viewImg.findings?"#0F172A":"#94A3B8", lineHeight:1.6 }}>
                    {viewImg.findings || "No findings recorded yet."}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {confirmDel && (
          <div className="modal-overlay">
            <div className="modal" style={{ maxWidth:380 }}>
              <div className="modal-header"><div style={{ fontWeight:700 }}>Delete Imaging Record</div></div>
              <div className="modal-body"><div className="alert alert-red">This will permanently delete the image and all associated data.</div></div>
              <div className="modal-footer">
                <button className="btn btn-ghost" onClick={() => setConfirmDel(null)}>Cancel</button>
                <button className="btn btn-danger" onClick={() => handleDelete(confirmDel)}>Delete</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  const filterBtn = (active) => ({
    padding:"4px 12px", borderRadius:99, fontSize:12, cursor:"pointer", border:"1px solid",
    background: active ? "#0F172A" : "#fff",
    borderColor: active ? "#0F172A" : "#E4E8F0",
    color: active ? "#fff" : "#475569", fontWeight: active ? 700 : 400,
  });

  return (
    <div>
      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20 }}>
        <div>
          <h1 style={{ fontSize:20, fontWeight:700, marginBottom:2 }}>Imaging Register</h1>
          <p style={{ color:"#64748B", fontSize:13 }}>X-Ray, CT, MRI, Ultrasound and all imaging studies</p>
        </div>
        {canUpload && (
          <button className="btn btn-primary" onClick={() => setShowUpload(true)}>+ Upload Imaging</button>
        )}
      </div>

      {/* Stats */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:16 }}>
        {[
          { label:"Total Studies", value:stats.total, color:"#4F46E5" },
          { label:"Today", value:stats.today, color:"#059669" },
          { label:"Modalities", value:stats.modalities, color:"#7C3AED" },
          { label:"Patients", value:stats.patients, color:"#0D9488" },
        ].map(s => (
          <div key={s.label} className="card" style={{ padding:"14px 18px" }}>
            <div style={{ fontSize:11, fontWeight:700, color:"#94A3B8", textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:6 }}>{s.label}</div>
            <div style={{ fontSize:28, fontWeight:800, color:s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="card" style={{ padding:"14px 20px", marginBottom:12 }}>
        <div className="search-wrap" style={{ marginBottom:12 }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input className="input" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by patient name, EMR ID, modality, description, radiologist..." />
        </div>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
          <span style={{ fontSize:11, fontWeight:700, color:"#94A3B8", textTransform:"uppercase" }}>Date:</span>
          {DATE_OPTIONS.map(d => <button key={d} style={filterBtn(dateFilter===d)} onClick={() => setDateFilter(d)}>{d}</button>)}
          <span style={{ fontSize:11, fontWeight:700, color:"#94A3B8", textTransform:"uppercase", marginLeft:8 }}>Type:</span>
          {["All",...MODALITIES].map(m => (
            <button key={m} style={{
              ...filterBtn(modalityFilter===m),
              background: modalityFilter===m ? (MODALITY_COLOR[m]?.color||"#0F172A") : "#fff",
              borderColor: modalityFilter===m ? (MODALITY_COLOR[m]?.color||"#0F172A") : "#E4E8F0",
              color: modalityFilter===m ? "#fff" : "#475569",
            }} onClick={() => setModality(m)}>{m}</button>
          ))}
          <div style={{ marginLeft:"auto", display:"flex", gap:6 }}>
            <button style={filterBtn(view==="register")} onClick={() => setView("register")}>
              ☰ List
            </button>
            <button style={filterBtn(view==="grid")} onClick={() => setView("grid")}>
              ⊞ Grid
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="card">
        {loading ? (
          <div className="empty-state"><p>Loading imaging records...</p></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="48" height="48" style={{ margin:"0 auto 12px", display:"block", color:"#CBD5E1" }}>
              <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
            </svg>
            <h3>{search ? "No records found" : "No imaging records"}</h3>
            <p>{canUpload ? "Upload an imaging study to get started." : "No imaging available."}</p>
          </div>
        ) : view === "register" ? (
          /* ── Register / List view ── */
          <div style={{ overflowX:"auto" }}>
            <div style={{ display:"grid", gridTemplateColumns:"90px 1fr 110px 90px 80px 1fr 80px", padding:"10px 20px", background:"#F8F9FC", borderBottom:"2px solid #E4E8F0" }}>
              {["Date","Patient","Modality","Time","Study","Findings","Action"].map(h => (
                <div key={h} style={{ fontSize:11, fontWeight:700, color:"#94A3B8", textTransform:"uppercase", letterSpacing:"0.5px" }}>{h}</div>
              ))}
            </div>
            {filtered.map((img, idx) => {
              const mc = MODALITY_COLOR[img.modality] || MODALITY_COLOR.Other;
              return (
                <div key={img.id} style={{
                  display:"grid", gridTemplateColumns:"90px 1fr 110px 90px 80px 1fr 80px",
                  padding:"12px 20px", borderBottom:"1px solid #F1F5F9",
                  background: idx%2===0 ? "#fff" : "#FAFBFC",
                  alignItems:"center", cursor:"pointer", transition:"background 0.12s",
                }}
                  onMouseEnter={e => e.currentTarget.style.background="#EFF6FF"}
                  onMouseLeave={e => e.currentTarget.style.background=idx%2===0?"#fff":"#FAFBFC"}
                  onClick={() => handleSetViewImg(img)}
                >
                  <div style={{ fontSize:12, fontWeight:600, color:"#475569" }}>{img.date}</div>
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <div style={{ width:28, height:28, borderRadius:"50%", background:"#EFF6FF", color:"#2563EB", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700, flexShrink:0 }}>
                      {img.patientName?.charAt(0)}
                    </div>
                    <div>
                      <div style={{ fontWeight:600, fontSize:13 }}>{img.patientName}</div>
                      <div style={{ fontSize:11, color:"#94A3B8" }}>{img.patientId}</div>
                    </div>
                  </div>
                  <div><span style={{ background:mc.bg, color:mc.color, padding:"2px 8px", borderRadius:99, fontSize:11, fontWeight:700 }}>{img.modality}</span></div>
                  <div style={{ fontSize:12, color:"#64748B" }}>{img.time||"—"}</div>
                  <div style={{ fontSize:12, color:"#475569" }}>{img.description||"—"}</div>
                  <div style={{ fontSize:12, color:"#64748B", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {img.findings ? img.findings.substring(0,60)+"…" : "—"}
                  </div>
                  <div>
                    <button className="btn btn-secondary btn-sm" style={{ fontSize:11 }}
                      onClick={e => { e.stopPropagation(); handleSetViewImg(img); }}>View →</button>
                  </div>
                </div>
              );
            })}
            <div style={{ padding:"10px 20px", borderTop:"1px solid #F1F5F9", fontSize:12, color:"#94A3B8" }}>
              Showing {filtered.length} record{filtered.length!==1?"s":""}
            </div>
          </div>
        ) : (
          /* ── Grid view ── */
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(260px,1fr))", gap:16, padding:16 }}>
            {filtered.map(img => {
              const mc = MODALITY_COLOR[img.modality] || MODALITY_COLOR.Other;
              const fileUrl = getImagingFileUrl(img.id);
              const isImg = isImageFile(img.file_name);
              return (
                <div key={img.id} style={{ border:"1px solid #E4E8F0", borderRadius:12, overflow:"hidden", cursor:"pointer", transition:"box-shadow 0.15s" }}
                  onClick={() => handleSetViewImg(img)}
                  onMouseEnter={e => e.currentTarget.style.boxShadow="0 4px 16px rgba(0,0,0,0.1)"}
                  onMouseLeave={e => e.currentTarget.style.boxShadow="none"}
                >
                  <div style={{ background:"#0F172A", height:160, display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden" }}>
                    {isImg ? (
                      <img src={fileUrl} alt={img.description||img.modality}
                        style={{ width:"100%", height:"100%", objectFit:"cover" }}
                        onError={e => { e.target.style.display="none"; }} />
                    ) : (
                      <div style={{ textAlign:"center", color:"#64748B" }}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="40" height="40"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                        <div style={{ fontSize:11, marginTop:6 }}>{img.file_name}</div>
                      </div>
                    )}
                  </div>
                  <div style={{ padding:"12px 14px" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6 }}>
                      <span style={{ background:mc.bg, color:mc.color, padding:"2px 8px", borderRadius:5, fontSize:11, fontWeight:700 }}>{img.modality}</span>
                      <span style={{ fontSize:11, color:"#94A3B8" }}>{img.date}</span>
                    </div>
                    <div style={{ fontWeight:700, fontSize:13, marginBottom:2 }}>{img.patientName}</div>
                    <div style={{ fontSize:12, color:"#64748B" }}>{img.description||"—"}</div>
                    {img.findings && (
                      <div style={{ fontSize:11, color:"#475569", background:"#F8F9FC", borderRadius:6, padding:"5px 8px", marginTop:6, borderLeft:"2px solid #CBD5E1" }}>
                        {img.findings.substring(0,80)}{img.findings.length>80?"…":""}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showUpload && (
        <UploadImagingModal
          patients={patients}
          addPatient={addPatient}
          onClose={() => setShowUpload(false)}
          onDone={(newImg) => { setAllImaging(prev => [newImg, ...prev]); setShowUpload(false); }}
        />
      )}
    </div>
  );
}

// ── Upload Modal with search + quick register ─────────────────────────────────
function UploadImagingModal({ patients, addPatient, onClose, onDone }) {
  const { addImaging } = useApp();
  const [mode, setMode]           = useState("search"); // "search" | "new"
  const [patientSearch, setPSearch] = useState("");
  const [selectedPatient, setSelPt] = useState(null);
  const [file, setFile]           = useState(null);
  const [dragging, setDrag]       = useState(false);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState("");

  // New patient form
  const [newPt, setNewPt] = useState({ name:"", age:"", sex:"Female", blood:"B+", phoneDigits:"", doctor:"", address:"" });
  const setNP = (k,v) => setNewPt(f => ({...f,[k]:v}));

  // Imaging form
  const [form, setForm] = useState({
    modality:"X-Ray",
    date: new Date().toISOString().split("T")[0],
    time:"", description:"", findings:"", radiologist:""
  });
  const set = (k,v) => setForm(f => ({...f,[k]:v}));

  const searchedPatients = patients.filter(p => {
    const q = patientSearch.toLowerCase();
    return q.length > 0 && (
      p.name?.toLowerCase().includes(q) ||
      p.id?.toLowerCase().includes(q) ||
      p.phone?.includes(patientSearch)
    );
  }).slice(0,8);

  const phoneResult = validatePhoneDigits(newPt.phoneDigits);

  const submit = async () => {
    setError(""); setLoading(true);
    try {
      let patientId = selectedPatient?.id;
      let patientName = selectedPatient?.name;

      // Quick register new patient
      if (mode === "new") {
        if (!newPt.name.trim()) { setError("Patient name is required."); setLoading(false); return; }
        if (!newPt.age) { setError("Age is required."); setLoading(false); return; }
        if (!phoneResult.valid) { setError(phoneResult.message); setLoading(false); return; }
        const created = await addPatient({ ...newPt, phone: phoneResult.normalised, age: parseInt(newPt.age) });
        patientId = created;
        patientName = newPt.name;
      }

      if (!patientId) { setError("Please select or register a patient."); setLoading(false); return; }
      if (!file) { setError("Please select an image file."); setLoading(false); return; }

      const result = await addImaging(patientId, form, file);
      onDone({ ...result, patientName, patientId });
    } catch (e) {
      setError(e.message || "Upload failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <div style={{ fontWeight:700, fontSize:16 }}>Upload Imaging Study</div>
          <button className="btn btn-icon btn-ghost" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-red">{error}</div>}

          {/* Patient selection tabs */}
          <div style={{ display:"flex", gap:0, marginBottom:16, border:"1px solid #E4E8F0", borderRadius:8, overflow:"hidden" }}>
            <button onClick={() => { setMode("search"); setSelPt(null); }} style={{
              flex:1, padding:"10px", fontSize:13, fontWeight:600, cursor:"pointer", border:"none",
              background: mode==="search" ? "#0F172A" : "#F8F9FC", color: mode==="search" ? "#fff" : "#475569",
            }}>🔍 Search Existing Patient</button>
            <button onClick={() => { setMode("new"); setSelPt(null); }} style={{
              flex:1, padding:"10px", fontSize:13, fontWeight:600, cursor:"pointer", border:"none",
              background: mode==="new" ? "#0F172A" : "#F8F9FC", color: mode==="new" ? "#fff" : "#475569",
              borderLeft:"1px solid #E4E8F0",
            }}>+ Quick Register New Patient</button>
          </div>

          {mode === "search" ? (
            <div className="form-group">
              <label className="form-label">Search Patient *</label>
              <div className="search-wrap" style={{ marginBottom:8 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                <input className="input" value={patientSearch} onChange={e => { setPSearch(e.target.value); setSelPt(null); }}
                  placeholder="Type patient name, EMR ID or phone..." autoFocus />
              </div>
              {selectedPatient && (
                <div style={{ display:"flex", alignItems:"center", gap:10, background:"#EFF6FF", border:"1px solid #BFDBFE", borderRadius:8, padding:"10px 14px", marginBottom:8 }}>
                  <div style={{ width:32, height:32, borderRadius:"50%", background:"#2563EB", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700 }}>
                    {selectedPatient.name?.charAt(0)}
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700 }}>{selectedPatient.name}</div>
                    <div style={{ fontSize:12, color:"#64748B" }}>{selectedPatient.id} · {selectedPatient.age}y · {selectedPatient.sex}</div>
                  </div>
                  <button className="btn btn-ghost btn-sm" onClick={() => { setSelPt(null); setPSearch(""); }}>✕</button>
                </div>
              )}
              {!selectedPatient && searchedPatients.length > 0 && (
                <div style={{ border:"1px solid #E4E8F0", borderRadius:8, overflow:"hidden", maxHeight:200, overflowY:"auto" }}>
                  {searchedPatients.map(p => (
                    <div key={p.id} style={{ padding:"10px 14px", cursor:"pointer", borderBottom:"1px solid #F1F5F9", display:"flex", alignItems:"center", gap:10 }}
                      onMouseEnter={e => e.currentTarget.style.background="#F8F9FC"}
                      onMouseLeave={e => e.currentTarget.style.background="#fff"}
                      onClick={() => { setSelPt(p); setPSearch(p.name); }}
                    >
                      <div style={{ width:28, height:28, borderRadius:"50%", background:"#EFF6FF", color:"#2563EB", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700 }}>
                        {p.name?.charAt(0)}
                      </div>
                      <div>
                        <div style={{ fontWeight:600, fontSize:13 }}>{p.name}</div>
                        <div style={{ fontSize:11, color:"#94A3B8" }}>{p.id} · {p.age}y · {p.sex} · {p.phone||"—"}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {!selectedPatient && patientSearch.length > 0 && searchedPatients.length === 0 && (
                <div style={{ padding:"12px 14px", color:"#94A3B8", fontSize:13, border:"1px solid #E4E8F0", borderRadius:8, textAlign:"center" }}>
                  No patient found. <button className="btn btn-ghost btn-sm" onClick={() => setMode("new")} style={{ color:"#2563EB" }}>Register as new patient →</button>
                </div>
              )}
            </div>
          ) : (
            /* Quick register form */
            <div>
              <div style={{ fontSize:12, color:"#64748B", marginBottom:12, padding:"8px 12px", background:"#F8F9FC", borderRadius:6 }}>
                Patient will be registered and imaging uploaded in one step.
              </div>
              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">Full Name *</label>
                  <input className="input" value={newPt.name} onChange={e => setNP("name",e.target.value)} placeholder="Patient name" autoFocus />
                </div>
                <div className="form-group">
                  <label className="form-label">Age *</label>
                  <input className="input" type="number" value={newPt.age} onChange={e => setNP("age",e.target.value)} placeholder="Years" min="0" max="150" />
                </div>
                <div className="form-group">
                  <label className="form-label">Sex</label>
                  <select className="select" value={newPt.sex} onChange={e => setNP("sex",e.target.value)}>
                    <option>Female</option><option>Male</option><option>Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Blood Group</label>
                  <select className="select" value={newPt.blood} onChange={e => setNP("blood",e.target.value)}>
                    {BLOOD_GROUPS.map(b => <option key={b}>{b}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Phone *</label>
                  <div style={{ display:"flex" }}>
                    <span style={{ padding:"0 10px", display:"flex", alignItems:"center", background:"#F1F5F9", border:"1px solid #E4E8F0", borderRight:"none", borderRadius:"6px 0 0 6px", fontSize:13, fontWeight:700, color:"#475569" }}>+880</span>
                    <input className="input" style={{ borderRadius:"0 6px 6px 0", borderLeft:"none" }}
                      value={newPt.phoneDigits}
                      onChange={e => setNP("phoneDigits", e.target.value.replace(/\D/g,"").slice(0,10))}
                      placeholder="1712345678" maxLength={10} inputMode="numeric" />
                  </div>
                  {newPt.phoneDigits && <div style={{ fontSize:11, marginTop:3, color: phoneResult.valid?"#16A34A":"#DC2626" }}>
                    {phoneResult.valid ? `✓ ${phoneResult.normalised}` : phoneResult.message}
                  </div>}
                </div>
                <div className="form-group">
                  <label className="form-label">Referred Doctor</label>
                  <input className="input" value={newPt.doctor} onChange={e => setNP("doctor",e.target.value)} placeholder="Dr. Name" />
                </div>
              </div>
            </div>
          )}

          <div style={{ borderTop:"1px solid #E4E8F0", marginTop:16, paddingTop:16 }}>
            <div style={{ fontSize:12, fontWeight:700, color:"#475569", textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:12 }}>Imaging Details</div>
            <div className="form-grid-2">
              <div className="form-group">
                <label className="form-label">Modality *</label>
                <select className="select" value={form.modality} onChange={e => set("modality",e.target.value)}>
                  {MODALITIES.map(m => <option key={m}>{m}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Study Date</label>
                <input className="input" type="date" value={form.date} onChange={e => set("date",e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <input className="input" value={form.description} onChange={e => set("description",e.target.value)} placeholder="e.g. Chest PA view" />
              </div>
              <div className="form-group">
                <label className="form-label">Radiologist</label>
                <input className="input" value={form.radiologist} onChange={e => set("radiologist",e.target.value)} placeholder="Radiologist name" />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Preliminary Findings</label>
              <textarea className="input" value={form.findings} onChange={e => set("findings",e.target.value)} rows={2} placeholder="Optional..." style={{ resize:"vertical" }} />
            </div>
            <div className={`upload-area ${dragging?"dragging":""}`}
              onDragOver={e => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)}
              onDrop={e => { e.preventDefault(); setDrag(false); setFile(e.dataTransfer.files[0]); }}
              onClick={() => document.getElementById("img-input").click()}
            >
              <input id="img-input" type="file" accept="image/*,.pdf,.dcm" style={{ display:"none" }} onChange={e => setFile(e.target.files[0])} />
              {file ? (
                <div>
                  <div style={{ fontWeight:700, fontSize:14, color:"#4F46E5" }}>{file.name}</div>
                  <div style={{ fontSize:12, color:"#64748B", marginTop:4 }}>{(file.size/1024).toFixed(1)} KB · <strong style={{ color:"#4F46E5" }}>Click to change</strong></div>
                </div>
              ) : (
                <div>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="36" height="36" style={{ margin:"0 auto 10px", display:"block", color:"#CBD5E1" }}>
                    <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
                  </svg>
                  <div style={{ fontWeight:700, fontSize:14, color:"#0F172A", marginBottom:4 }}>Drop image here or click to browse</div>
                  <div style={{ fontSize:12, color:"#64748B" }}>Supports JPG, PNG, DICOM, PDF</div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={loading}>
            {loading ? "Uploading..." : mode==="new" ? "Register & Upload" : "Upload Imaging"}
          </button>
        </div>
      </div>
    </div>
  );
}